import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClockCountdown, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtBRL } from "@/lib/format";

const WaitlistRow = ({ entry, index, onNotify, onRemove }) => (
    <div data-testid={`wl-entry-${entry.id}`} className="px-4 py-3.5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full border gold-border flex items-center justify-center text-gold text-base metric-num">{index + 1}</div>
        <div className="flex-1">
            <div className="text-base flex items-center gap-2">
                {entry.player_name}
                {entry.intended_buyin && (
                    <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border border-gold/40 text-gold">
                        {fmtBRL(entry.intended_buyin)}
                        {entry.intended_method && entry.intended_method !== "cash" && ` · ${entry.intended_method}`}
                    </span>
                )}
            </div>
            <div className="text-sm text-muted-foreground font-mono-plex">{entry.player_phone}</div>
        </div>
        <Button data-testid={`btn-notify-${entry.id}`} onClick={() => onNotify(entry)} className="btn-gold h-10 px-4">
            <PaperPlaneTilt size={16} className="mr-1.5" /> Send SMS
        </Button>
        <Button data-testid={`btn-remove-wl-${entry.id}`} onClick={() => onRemove(entry.id)} variant="ghost" size="icon">
            <X size={16} />
        </Button>
    </div>
);

const TableWaitlistGroup = ({ table, entries, onNotify, onRemove }) => (
    <div className="border border-border rounded-md">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/60 bg-secondary/40">
            <div className="flex items-center gap-3">
                <span className="font-serif text-lg">{table.name}</span>
                <span className="label-mini">{table.game_type} · {fmtBRL(table.small_blind)}/{fmtBRL(table.big_blind)}</span>
            </div>
            <span className="text-xs text-muted-foreground">{entries.length} in queue</span>
        </div>
        <div className="divide-y divide-border/40">
            {entries.length === 0 && <div className="px-4 py-3 text-sm text-muted-foreground">No players waiting.</div>}
            {entries.map((e, i) => (
                <WaitlistRow key={e.id} entry={e} index={i} onNotify={onNotify} onRemove={onRemove} />
            ))}
        </div>
    </div>
);

export default function WaitlistManager({ tables, players, waitlistMap, onAdd, onNotify, onRemove }) {
    const [tableId, setTableId] = useState("");
    const [playerId, setPlayerId] = useState("");

    const add = async () => {
        if (!tableId || !playerId) { toast.error("Select table and player"); return; }
        try {
            await onAdd(tableId, playerId);
            toast.success("Added to waitlist");
            setPlayerId("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add");
        }
    };

    return (
        <Card className="card-lux lg:col-span-8">
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <ClockCountdown weight="duotone" size={22} className="text-gold" />
                        <h3 className="font-serif text-xl">Waitlist</h3>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-6">
                    <div>
                        <Label className="label-mini">Table</Label>
                        <Select value={tableId} onValueChange={setTableId}>
                            <SelectTrigger data-testid="wl-select-table"><SelectValue placeholder="Pick a table" /></SelectTrigger>
                            <SelectContent>
                                {tables.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.game_type}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-mini">Player</Label>
                        <Select value={playerId} onValueChange={setPlayerId}>
                            <SelectTrigger data-testid="wl-select-player"><SelectValue placeholder="Pick a player" /></SelectTrigger>
                            <SelectContent>
                                {players.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button data-testid="btn-add-waitlist" onClick={add} className="btn-gold h-11">Add to waitlist</Button>
                </div>

                <div className="space-y-5">
                    {tables.map(t => (
                        <TableWaitlistGroup
                            key={t.id}
                            table={t}
                            entries={(waitlistMap[t.id] || []).filter(e => !e.notified)}
                            onNotify={onNotify}
                            onRemove={onRemove}
                        />
                    ))}
                    {tables.length === 0 && (
                        <div className="text-sm text-muted-foreground p-6 text-center border border-dashed border-border rounded-md">
                            No active tables. Open a new table to get started.
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
